import { defineStore } from 'pinia'
import { ref } from 'vue'
import { HomeAPI } from '@/services/home'
// import { getCachedRoleInfo, setCachedRoleInfo } from '@/indexDb/roleInfoIndexeddb'
// import { chatDB } from '@/indexDb/chatDB'

export interface ExploreProfile {
  nickname?: string
  gender?: number
  age?: number
  mood?: string
  head?: string
  cover?: string
}

export interface ExploreUser {
  id: number
  profile?: ExploreProfile
  role?: number
  is_online?: boolean
  is_busy?: boolean
  followed_at?: number
  anchor?: any
  chat_cover?: string
}

interface ExploreListResponse {
  error_code: number
  message?: string
  data?: {
    anchors?: ExploreUser[]
  }
}

export const useExploreStore = defineStore('explore', () => {
  const users = ref<ExploreUser[]>([])
  const page = ref(1)
  const pageSize = ref(20)
  const loading = ref(false)
  const loadingMore = ref(false)
  const hasMore = ref(true)
  const initialized = ref(false)
  const error = ref<string | null>(null)
  const roleDetails = ref<Record<number, any>>({})
  // 加载失败，刷新时显示
  const hasError = ref(false)
  // 骨架屏加载中，刷新时显示
  const freshLoading = ref(false)

  const normalizeUsers = (res: ExploreListResponse): ExploreUser[] => {
    const list = res?.data
    return Array.isArray(list) ? list : []
  }

  const fetchPage = async (p: number) => {
    const res = (await HomeAPI.getRoleList({
      page: p,
      pageSize: pageSize.value,
      type: 2
    })) as any

    if (res?.code !== 200) {
      throw new Error(res?.message || 'Failed to load explore list')
    }
    return normalizeUsers(res)
  }

  const refresh = async () => {
    if (loading.value) return
    freshLoading.value = true
    hasError.value = false
    loading.value = true
    error.value = null
    try {
      page.value = 1
      const data = await fetchPage(1)
      users.value = data
      hasMore.value = data.length >= pageSize.value
      initialized.value = true
      freshLoading.value = false
      hasError.value = false
    } catch (e: any) {
      error.value = e?.message || 'Failed to load explore list'
      users.value = []
      hasMore.value = false
      freshLoading.value = false
      hasError.value = true
      throw e
    } finally {
      loading.value = false
    }
  }

  const loadMore = async () => {
    if (loadingMore.value || loading.value) return

    // 没有更多数据时，从头重新加载
    if (!hasMore.value && page.value > 1) {
      page.value = 1
      hasMore.value = true
    }

    loadingMore.value = true
    try {
      const next = page.value + 1
      const data = await fetchPage(next)
      users.value = users.value.concat(data)
      page.value = next
      hasMore.value = data.length >= pageSize.value
    } catch (e) {
      // 加载更多失败不清空已有数据
      console.error('Explore loadMore failed:', e)
    } finally {
      loadingMore.value = false
    }
  }

  const getRoleInfo = async (id: number) => {
    if (!id) return null
    // 1️⃣ 内存缓存命中
    if (roleDetails.value[id]) {
      return roleDetails.value[id]
    }

    // 2️⃣ IndexedDB 角色信息缓存命中
    try {
    } catch (e) {
      console.warn('read roleInfo from IndexedDB failed:', e)
    }

    // 3️⃣ 调接口获取角色信息
    try {
      const res = await HomeAPI.getRoleList({
        user_id: Number(id),
        "v": 2,
      })

      if ((res as any)?.data?.messages?.length > 0) {
        const data = (res as any).data
        roleDetails.value = {
          ...roleDetails.value,
          [id]: data
        }
        // setCachedRoleInfo(id, data)
        return data
      }
    } catch (e) {
      console.warn('getRoleInfo API failed:', e)
    }

    // 4️⃣ 接口无数据或失败，从本地聊天记录获取
    try {
      const currentUserId = Number(localStorage.getItem('userId')) || 0

      if (currentUserId) {
        // const localChat = await chatDB.get(currentUserId, id)


      }
    } catch (e) {
      console.warn('read local chat failed:', e)
    }

    // 5️⃣ 都没有数据，返回默认值
    const defaultData = { messages: [{ content: { text: '' } }] }
    roleDetails.value = {
      ...roleDetails.value,
      [id]: defaultData
    }
    return defaultData
  }

  const reset = () => {
    users.value = []
    page.value = 1
    hasMore.value = true
    initialized.value = false
    loading.value = false
    loadingMore.value = false
    error.value = null
    roleDetails.value = {}
  }

  return {
    users,
    page,
    pageSize,
    loading,
    loadingMore,
    hasMore,
    initialized,
    error,
    roleDetails,
    hasError,
    freshLoading,
    refresh,
    loadMore,
    reset,
    getRoleInfo
  }
})

